import { useState, useEffect, useCallback } from "react";
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    orderBy,
    getFirestore,
} from "firebase/firestore";
import firebaseApp from "../../../../firebase/credenciales";
import Swal from "sweetalert2";

const db = getFirestore(firebaseApp);

export const useOcurrencias = (usuario) => {
    const [ocurrencias, setOcurrencias] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtros, setFiltros] = useState({
        periodo: "",
        empleado_id: "",
        tipo: "",
        estado: "",
        justificado: "",
    });

    const cargarEmpleados = useCallback(async () => {
        if (!usuario?.ruc) return;
        try {
            const q = query(
                collection(db, "pl_empleados"),
                where("ruc_empresa", "==", usuario.ruc),
                where("activo", "==", true)
            );

            const querySnapshot = await getDocs(q);
            const empleadosData = [];

            querySnapshot.forEach((doc) => {
                empleadosData.push({ id: doc.id, ...doc.data() });
            });

            setEmpleados(empleadosData);
        } catch (error) {
            console.error("Error al cargar empleados:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Error al cargar empleados",
            });
        }
    }, [usuario?.ruc]);

    useEffect(() => {
        cargarEmpleados();
    }, [cargarEmpleados]);

    const cargarOcurrencias = useCallback(async () => {
        try {
            setLoading(true);

            if (!usuario?.ruc) {
                throw new Error("Usuario no tiene RUC configurado");
            }

            if (filtros.periodo && !/^\d{4}-\d{2}$/.test(filtros.periodo)) {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "El periodo debe tener formato YYYY-MM",
                });
                setOcurrencias([]);
                return;
            }

            console.log("📊 Cargando ocurrencias con filtros:", filtros);

            let q = query(
                collection(db, "pl_ocurrencias"),
                where("ruc_empresa", "==", usuario.ruc),
                orderBy("fecha", "desc")
            );

            if (filtros.periodo) {
                q = query(q, where("periodo", "==", filtros.periodo));
            }

            if (filtros.empleado_id && filtros.empleado_id.trim()) {
                q = query(q, where("empleado_id", "==", filtros.empleado_id.trim()));
            }

            if (filtros.tipo && ['falta', 'tardanza', 'permiso', 'vacaciones', 'licencia', 'descanso_medico', 'he_25', 'he_35', 'he_100'].includes(filtros.tipo)) {
                q = query(q, where("tipo", "==", filtros.tipo));
            }

            if (filtros.estado && filtros.estado.trim() !== "") {
                q = query(q, where("estado", "==", filtros.estado));
            }

            const querySnapshot = await getDocs(q);
            const ocurrenciasData = [];

            querySnapshot.forEach((doc) => {
                const data = { id: doc.id, ...doc.data() };

                if (!data.fecha || !data.empleado_id || !data.tipo) {
                    return;
                }

                if (filtros.justificado !== "") {
                    const esJustificado = filtros.justificado === "true";
                    if (data.justificado !== esJustificado) {
                        return;
                    }
                }

                ocurrenciasData.push({
                    ...data,
                    dias_afectados: data.dias_afectados || 0,
                    minutos_tardanza: data.minutos_tardanza || 0,
                    justificado: data.justificado || false,
                    estado: data.estado || "PENDIENTE"
                });
            });

            setOcurrencias(ocurrenciasData);
        } catch (error) {
            console.error("❌ Error al cargar ocurrencias:", error);
            let mensajeError = "Error al cargar ocurrencias";

            if (error.message?.includes('permission-denied')) {
                mensajeError = "No tiene permisos para acceder a las ocurrencias";
            } else if (error.message?.includes('failed-precondition')) {
                mensajeError = "Los filtros aplicados son demasiado complejos. Intente con menos criterios";
            }

            Swal.fire({
                icon: "error",
                title: "Error",
                text: mensajeError,
            });
            setOcurrencias([]);
        } finally {
            setLoading(false);
        }
    }, [filtros, usuario?.ruc]);

    // Cargar ocurrencias automáticamente cuando cambia el periodo
    useEffect(() => {
        if (filtros.periodo) {
            cargarOcurrencias();
        }
    }, [filtros.periodo, cargarOcurrencias]);

    const eliminarOcurrencia = async (ocurrenciaId) => {
        const result = await Swal.fire({
            title: "¿Eliminar ocurrencia?",
            text: "Esta acción no se puede deshacer",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, "pl_ocurrencias", ocurrenciaId));

                Swal.fire({
                    icon: "success",
                    title: "Eliminado",
                    text: "La ocurrencia se eliminó correctamente",
                });

                cargarOcurrencias();
            } catch (error) {
                console.error("Error al eliminar:", error);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Error al eliminar la ocurrencia",
                });
            }
        }
    };

    const actualizarOcurrencia = async (ocurrenciaActualizada) => {
        try {
            const docRef = doc(db, "pl_ocurrencias", ocurrenciaActualizada.id);
            await updateDoc(docRef, {
                justificado: ocurrenciaActualizada.justificado,
                con_goce: ocurrenciaActualizada.con_goce,
                estado: ocurrenciaActualizada.estado,
                motivo: ocurrenciaActualizada.motivo,
                observaciones: ocurrenciaActualizada.observaciones,
                aprobado_por: usuario.email,
                fecha_actualizacion: new Date(),
            });

            Swal.fire({
                icon: "success",
                title: "Actualizado",
                text: "La ocurrencia se actualizó correctamente",
            });

            cargarOcurrencias();
            return true;
        } catch (error) {
            console.error("Error al guardar cambios:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Error al guardar cambios",
            });
            return false;
        }
    };

    const limpiarFiltros = () => {
        setFiltros({
            periodo: "",
            empleado_id: "",
            tipo: "",
            estado: "",
            justificado: "",
        });
        setOcurrencias([]);
    };

    return {
        ocurrencias,
        empleados,
        loading,
        filtros,
        setFiltros,
        cargarOcurrencias,
        eliminarOcurrencia,
        actualizarOcurrencia,
        limpiarFiltros
    };
};
